import { exportRegistrations } from 'src/services/arlo/arlo-service';
import { WorkflowResponse } from '../../../types';
import { Order } from '../../Table/types';

const WORKFLOW_MESSAGES = {
  approveSuccess: 'Registration has been successfully approved.',
  rejectSuccess: 'Registration has been successfully rejected.',
  alreadyApproved: 'This registration has already been approved.',
  alreadyRejected: 'This registration has already been rejected.',
  genericError: 'There was an error updating this registration.',
} as const;

const extractBackendMessage = (source: unknown): string => {
  const axiosMessage = (
    source as { response?: { data?: { message?: string } } }
  )?.response?.data?.message;
  if (typeof axiosMessage === 'string' && axiosMessage.trim()) {
    return axiosMessage;
  }

  if (typeof source === 'object' && source !== null && 'message' in source) {
    const message = (source as WorkflowResponse).message;
    if (typeof message === 'string') return message;
  }

  return '';
};

export const mapWorkflowNotification = (
  source: WorkflowResponse | unknown,
  action: 'approve' | 'reject',
  isError = false
): { message: string; variant: 'success' | 'warning' | 'error' } => {
  const normalized = extractBackendMessage(source).toLowerCase();

  if (normalized.includes('already approved')) {
    return {
      message: WORKFLOW_MESSAGES.alreadyApproved,
      variant: 'warning',
    };
  }

  if (normalized.includes('already rejected')) {
    return {
      message: WORKFLOW_MESSAGES.alreadyRejected,
      variant: 'warning',
    };
  }

  if (isError) {
    return { message: WORKFLOW_MESSAGES.genericError, variant: 'error' };
  }

  return {
    message:
      action === 'approve'
        ? WORKFLOW_MESSAGES.approveSuccess
        : WORKFLOW_MESSAGES.rejectSuccess,
    variant: 'success',
  };
};

export enum Columns {
  OrderCode = 'orderCode',
  Organisation = 'organisation',
  CourseName = 'courseName',
  Cost = 'cost',
  Attendee = 'attendee',
  Action = 'action',
}

interface HeadCell {
  id: Columns;
  label: string;
  order: Order;
}

export interface Row {
  cells: {
    [Columns.OrderCode]?: React.ReactElement;
    [Columns.Organisation]: React.ReactElement;
    [Columns.CourseName]: React.ReactElement;
    [Columns.Cost]?: React.ReactElement;
    [Columns.Attendee]: React.ReactElement;
    [Columns.Action]?: React.ReactElement;
  };
}

export const headCellsPending: HeadCell[] = [
  { id: Columns.OrderCode, label: 'Order Number', order: Order.NA },
  { id: Columns.Organisation, label: 'Organisation', order: Order.NA },
  { id: Columns.CourseName, label: 'Course Name', order: Order.NA },
  { id: Columns.Cost, label: 'Cost', order: Order.NA },
  { id: Columns.Attendee, label: 'Attendee', order: Order.NA },
  { id: Columns.Action, label: 'Action', order: Order.NA },
];

export const headCellsApproved: HeadCell[] = [
  { id: Columns.OrderCode, label: 'Order Number', order: Order.NA },
  { id: Columns.Organisation, label: 'Organisation', order: Order.NA },
  { id: Columns.CourseName, label: 'Course Name', order: Order.NA },
  { id: Columns.Cost, label: 'Cost', order: Order.NA },
  { id: Columns.Attendee, label: 'Attendee', order: Order.NA },
];

export const headCellsRejected: HeadCell[] = [
  { id: Columns.Organisation, label: 'Organisation', order: Order.NA },
  { id: Columns.CourseName, label: 'Course Name', order: Order.NA },
  { id: Columns.Attendee, label: 'Attendee', order: Order.NA },
];

export interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const triggerBlobDownload = (blob: Blob, centreNumber: string): void => {
  const blobUrl = URL.createObjectURL(blob);
  const filename = `bookings-${centreNumber}-${Date.now()}.csv`;

  const downloadViaAnchor = (targetDocument: Document): boolean => {
    const link = targetDocument.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    link.style.display = 'none';
    targetDocument.body.appendChild(link);
    link.click();
    targetDocument.body.removeChild(link);
    return true;
  };

  try {
    const targetWindow = window.top ?? window;
    downloadViaAnchor(targetWindow.document);
  } catch {
    const popup = window.open('about:blank', '_blank');
    if (popup) {
      downloadViaAnchor(popup.document);
      popup.close();
    } else {
      window.open(blobUrl, '_top');
    }
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  }
};

export const exportBookings = async (centreNumber: string): Promise<void> => {
  const blob = await exportRegistrations(centreNumber);
  triggerBlobDownload(blob, centreNumber);
};
